/** Operational execution states — logist-facing, not internal engine enums. */
export type OperationalExecutionState =
  | 'loading_pending'
  | 'loading_in_progress'
  | 'loading_complete'
  | 'pending_transit'
  | 'in_transit'
  | 'unloading_pending'
  | 'unloading_in_progress'
  | 'unloading_complete'
  | 'delivered'
  | 'partially_delivered'
  | 'failed';

const OPERATIONAL_LABELS: Record<OperationalExecutionState, string> = {
  loading_pending: 'Ожидает погрузки',
  loading_in_progress: 'Идёт погрузка',
  loading_complete: 'Погрузка завершена',
  pending_transit: 'Готов к отправке',
  in_transit: 'В пути',
  unloading_pending: 'Ожидает выгрузки',
  unloading_in_progress: 'Идёт выгрузка',
  unloading_complete: 'Выгрузка завершена',
  delivered: 'Доставлено',
  partially_delivered: 'Частично доставлено',
  failed: 'Ошибка исполнения'
};

const OPERATIONAL_COLORS: Record<OperationalExecutionState, string> = {
  loading_pending: 'default',
  loading_in_progress: 'processing',
  loading_complete: 'success',
  pending_transit: 'blue',
  in_transit: 'processing',
  unloading_pending: 'default',
  unloading_in_progress: 'processing',
  unloading_complete: 'success',
  delivered: 'success',
  partially_delivered: 'warning',
  failed: 'error'
};

const BACKEND_STOP_STATE_MAP: Record<string, OperationalExecutionState> = {
  PENDING_LOADING: 'loading_pending',
  ARRIVED_FOR_LOADING: 'loading_pending',
  LOADING: 'loading_in_progress',
  LOADED: 'loading_complete',
  PENDING_UNLOADING: 'unloading_pending',
  ARRIVED_FOR_UNLOADING: 'unloading_pending',
  UNLOADING: 'unloading_in_progress',
  UNLOADED: 'unloading_complete',
  SKIPPED: 'delivered',
  FAILED: 'failed',
  PARTIALLY_EXECUTED: 'partially_delivered'
};

const BACKEND_CARGO_STATE_MAP: Record<string, OperationalExecutionState> = {
  PENDING: 'loading_pending',
  PARTIALLY_LOADED: 'partially_delivered',
  LOADED: 'loading_complete',
  IN_TRANSIT: 'in_transit',
  PARTIALLY_UNLOADED: 'partially_delivered',
  DELIVERED: 'delivered',
  FAILED: 'failed'
};

export function normalizeOperationalState(raw: string | null | undefined): OperationalExecutionState {
  const key = String(raw ?? '').trim();
  if (!key) return 'loading_pending';
  const lower = key.toLowerCase();
  if (lower in OPERATIONAL_LABELS) return lower as OperationalExecutionState;
  const upper = key.toUpperCase();
  return BACKEND_STOP_STATE_MAP[upper] ?? BACKEND_CARGO_STATE_MAP[upper] ?? 'loading_pending';
}

export function normalizeCargoOperationalState(raw: string | null | undefined): OperationalExecutionState {
  const key = String(raw ?? '').trim();
  if (!key) return 'loading_pending';
  const upper = key.toUpperCase();
  return BACKEND_CARGO_STATE_MAP[upper] ?? normalizeOperationalState(raw);
}

export function getExecutionStatusLabel(state: OperationalExecutionState | string): string {
  const normalized = typeof state === 'string' ? normalizeOperationalState(state) : state;
  return OPERATIONAL_LABELS[normalized] ?? OPERATIONAL_LABELS.loading_pending;
}

export function getExecutionStatusColor(state: OperationalExecutionState | string): string {
  const normalized = typeof state === 'string' ? normalizeOperationalState(state) : state;
  return OPERATIONAL_COLORS[normalized] ?? 'default';
}

export function getExecutionStatusBadgeProps(
  state: OperationalExecutionState | string
): { label: string; color: string } {
  const normalized = typeof state === 'string' ? normalizeOperationalState(state) : state;
  return {
    label: getExecutionStatusLabel(normalized),
    color: getExecutionStatusColor(normalized)
  };
}

export function isLoadingOperationalState(state: OperationalExecutionState): boolean {
  return (
    state === 'loading_pending' ||
    state === 'loading_in_progress' ||
    state === 'loading_complete'
  );
}

export function isUnloadingOperationalState(state: OperationalExecutionState): boolean {
  return (
    state === 'unloading_pending' ||
    state === 'unloading_in_progress' ||
    state === 'unloading_complete'
  );
}

export function isInProgressOperationalState(state: OperationalExecutionState): boolean {
  return state === 'loading_in_progress' || state === 'unloading_in_progress';
}

export function isPendingOperationalState(state: OperationalExecutionState): boolean {
  return state === 'loading_pending' || state === 'unloading_pending';
}

export function isCompleteOperationalState(state: OperationalExecutionState): boolean {
  return (
    state === 'loading_complete' ||
    state === 'unloading_complete' ||
    state === 'delivered'
  );
}
